import { useCallback, useEffect, useRef } from 'react'
import type { MapRef } from 'react-map-gl/mapbox'
import type { MapViewState, MapBounds } from './chatMapConstants'

export function useMapParentSync(
  mapRef: React.RefObject<MapRef | null>,
  isMapLoaded: boolean,
  onViewStateChange?: (viewState: MapViewState) => void,
  onBoundsChange?: (bounds: MapBounds) => void,
): { handleMoveEnd: () => void } {
  const onViewStateChangeRef = useRef(onViewStateChange)
  const onBoundsChangeRef = useRef(onBoundsChange)

  useEffect(() => {
    onViewStateChangeRef.current = onViewStateChange
    onBoundsChangeRef.current = onBoundsChange
  }, [onViewStateChange, onBoundsChange])

  const handleMoveEnd = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    const center = map.getCenter()
    onViewStateChangeRef.current?.({
      longitude: center.lng,
      latitude: center.lat,
      zoom: map.getZoom(),
    })

    const boundsHandler = onBoundsChangeRef.current
    if (!boundsHandler) return
    const bounds = map.getBounds()
    if (!bounds) return
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    boundsHandler({ west: sw.lng, south: sw.lat, east: ne.lng, north: ne.lat })
  }, [mapRef])

  useEffect(() => {
    if (!isMapLoaded) return
    handleMoveEnd()
  }, [handleMoveEnd, isMapLoaded])

  return { handleMoveEnd }
}
